/**
 * DeleteObservationConfirmationModal Component
 *
 * Simple confirmation modal for deleting an economic observation.
 */

// 1. External dependencies
import React from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@enfomentis/ui';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for DeleteObservationConfirmationModal
 */
export interface DeleteObservationConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes (cancel) */
  onClose: () => void;
  /** Callback when delete is confirmed */
  onConfirm: () => void;
  /** Whether delete is in progress */
  isDeleting?: boolean;
}

// =============================================================================
// Component
// =============================================================================

/**
 * DeleteObservationConfirmationModal Component
 */
export const DeleteObservationConfirmationModal: React.FC<DeleteObservationConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      placement="center"
    >
      <ModalContent>
        <ModalHeader>Delete Observation</ModalHeader>

        <ModalBody>
          <p className="text-sm text-text-secondary">
            Are you sure you want to delete this observation?
          </p>
          <p className="text-sm text-text-secondary mt-2">
            This action cannot be undone.
          </p>
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="danger"
            isDisabled={isDeleting}
            isLoading={isDeleting}
            onPress={() => {
              onConfirm();
              onClose();
            }}
          >
            Delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteObservationConfirmationModal;
